import { AlertTriangle } from 'lucide-react';

interface InactivityWarningModalProps {
  open: boolean;
  secondsUntilLogout: number;
  onStayActive: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Inactivity warning — app-wide modal that appears when the user has been
 * idle past the warning threshold and counts down to the 60-min force-
 * logout. Non-dismissable by backdrop click because auto-logout is the
 * whole point; Stay Active is one way out, but any real keyboard / pointer
 * activity also clears it via the idle module's listeners.
 *
 * The 15-min auto-clock-out has no warning by design — the rep should
 * notice their status flipped on the next interaction, and the
 * server-driven sweep is silent.
 */
export function InactivityWarningModal({
  open,
  secondsUntilLogout,
  onStayActive,
}: InactivityWarningModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-[2px] p-4">
      <div className="bg-card border border-amber-500/30 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border/60 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 stroke-[2] text-amber-600" />
          <h2 className="text-[0.9375rem] font-bold text-foreground">Inactivity Warning</h2>
        </div>
        <div className="p-5 space-y-4 text-center">
          <p className="text-[0.8125rem] text-muted-foreground">
            You've been inactive for a while. You'll be signed out automatically in
          </p>
          <div className="text-[2.25rem] font-bold tabular-nums text-amber-700 leading-none">
            {formatCountdown(secondsUntilLogout)}
          </div>
          <p className="text-[0.75rem] text-muted-foreground">
            Move your mouse, press any key, or click the button below to stay active.
          </p>
          <button
            onClick={onStayActive}
            className="w-full py-2.5 rounded-xl text-[0.8125rem] font-semibold bg-brand-green text-white hover:bg-brand-green/90 transition-all cursor-pointer shadow-sm"
          >
            Stay Active
          </button>
        </div>
      </div>
    </div>
  );
}
